interface CaseStudyImage {
  image: string
  alt: string
}

export interface CaseStudyImageSet {
  hero: CaseStudyImage
  problem: CaseStudyImage
  research: CaseStudyImage
  strategy: CaseStudyImage
  design: CaseStudyImage
  outcome: CaseStudyImage
}

/**
 * Each entry is one coordinated editorial shoot: a single palette, surface and
 * lighting direction carried through the hero, four chapters and outcome image.
 */
export const caseStudyImages: Record<string, CaseStudyImageSet> = {
  'bhoomi-coffee': {
    hero: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/4b56d634-1bde-47a8-b40c-acb9046d0684.jpg',
      alt: 'A speciality coffee design presentation with ecommerce boards, an unbranded kraft pouch, tasting cup and roasted beans',
    },
    problem: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/39525ee6-60e8-482b-9c81-4e78d128ad6f.jpg',
      alt: 'An unbranded coffee pouch and discount token set before a refined folded product card',
    },
    research: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/1d7bb453-755b-4f71-8f22-15e52e6c69ec.jpg',
      alt: 'Three anonymous kraft coffee pouches beside a fan of unlabelled paper cards and roasted beans',
    },
    strategy: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/a96a730d-d3b9-4d31-a0be-f1e3bfd68eec.jpg',
      alt: 'A central coffee pouch connected by a black line to three smaller paper modules and a terracotta marker',
    },
    design: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/66dbaa95-9727-4c81-98f8-04d0210f224a.jpg',
      alt: 'Coffee ecommerce interface boards with abstract bars, muted terracotta actions and a ceramic tasting cup',
    },
    outcome: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/4dc1a12d-12f5-4de9-898d-731da3879157.jpg',
      alt: 'A complete coffee ecommerce concept spread across three large boards with a kraft pouch and ceramic cup',
    },
  },
  'deshmukh-rao': {
    hero: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/bfb05c49-1f15-4985-bf4c-366071e193ff.jpg',
      alt: 'A formal text-first law firm website presentation beside a closed black notebook and fountain pen',
    },
    problem: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/22bfdaa1-1096-4a20-b811-d2d133a18be1.jpg',
      alt: 'A closed black notebook partially obscuring a disordered stack of anonymous case-record cards',
    },
    research: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/ebb42d5e-dc19-45e2-9dda-97f56f759cd3.jpg',
      alt: 'Anonymous professional-practice website printouts arranged in a measured review grid',
    },
    strategy: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/4e124a3e-850a-4055-8ad6-d8397a046a11.jpg',
      alt: 'A central matter record connected by precise hairlines to three smaller evidence cards',
    },
    design: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/a729e7eb-955c-47eb-ad5c-74730e16a423.jpg',
      alt: 'Text-first website boards with dark heading bars, grey metadata, hairline rules and a slate action block',
    },
    outcome: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/4d748b5b-a7f0-4f97-9f19-6d62100c9923.jpg',
      alt: 'A complete law firm website concept spread across large text-first boards with a black notebook',
    },
  },
  'prana-physio': {
    hero: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/ae44f702-368c-4958-9e6a-01db76566582.jpg',
      alt: 'A mobile-first appointment booking concept presented across device-shaped boards with a towel and resistance band',
    },
    problem: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/f5673969-e587-471f-84f7-f8e405dc2eb1.jpg',
      alt: 'A face-down phone beside a long folded form and one small clean appointment card',
    },
    research: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/3050053a-d023-443d-bcbd-75231dec5d95.jpg',
      alt: 'A modelled reception log made from blank cards, timing tabs and a closed phone',
    },
    strategy: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/c39bae9a-9888-4cee-9c70-22972df238e1.jpg',
      alt: 'Three bone-paper steps connected directly to one muted sage appointment tile',
    },
    design: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/7dc5a022-7035-4bed-955b-019dc1ace027.jpg',
      alt: 'Mobile booking interface boards with abstract fields beside a folded towel and resistance band',
    },
    outcome: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/0fef8338-7a26-4df6-b81c-cf0f48500895.jpg',
      alt: 'A complete mobile booking concept across four device-shaped boards with restrained clinic materials',
    },
  },
  'meridian-architects': {
    hero: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/1e246ca3-eb1f-4701-9eca-0e6c401e9921.jpg',
      alt: 'An architecture identity presentation with abstract monogram geometry, project records and material swatches',
    },
    problem: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/2d700dea-86bf-4674-8ec2-825606e10cba.jpg',
      alt: 'Nearly identical architecture portfolio cards overlap while one blank position card sits apart',
    },
    research: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/2be15222-076d-471f-a574-a5fe8afd0d87.jpg',
      alt: 'Abstract architecture practice-site printouts arranged as a disciplined archive with a metal ruler',
    },
    strategy: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/374979dc-103a-443d-b50c-18f81b4975a8.jpg',
      alt: 'A written-position card leading a precise grid of project records linked by hairline rules',
    },
    design: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/58b045d7-7fb0-40e9-bc08-f94483952f3d.jpg',
      alt: 'An abstract embossed monogram, typography specimen, project record and material swatches',
    },
    outcome: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/08ba67f2-887e-4838-8f4e-616de550f30c.jpg',
      alt: 'A complete architecture identity and website system spread across large printed boards',
    },
  },
  'northpulse-dashboard': {
    hero: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/357c0799-849b-48a1-8a94-a2d7d42d67bc.jpg',
      alt: 'A tablet displaying a calm analytics dashboard beside two large interface boards',
    },
    problem: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/6a23c53e-1a8a-4bc9-a8f7-391edc594a49.jpg',
      alt: 'Many anonymous metric cards surrounding one hidden black circular marker',
    },
    research: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/8ea777d7-952d-49e3-9b01-3f6c9e3ba836.jpg',
      alt: 'Anonymous analytics interface printouts arranged in a measured product review grid',
    },
    strategy: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/7026ee0b-2cef-426a-867b-b2fa5d2a48cc.jpg',
      alt: 'One large primary metric tile set above three quiet supporting modules',
    },
    design: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/b10eb3d7-17c6-462c-83e0-68c4944e5537.jpg',
      alt: 'A tablet showing a calm abstract dashboard beside a set of printed component tiles',
    },
    outcome: {
      image:
        'https://cdn.magicpatterns.com/patterns/generated-images/65cff47f-5be6-41c3-986d-5665804c734e.jpg',
      alt: 'A complete analytics product concept across a tablet and three large interface boards',
    },
  },
}

export function getCaseStudyImages(slug: string): CaseStudyImageSet {
  return caseStudyImages[slug]
}
